import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { useActivity, timeAgo } from "../lib/activity";

export default function NotificationBell({ testId = "notif-bell" }) {
  const { notifications = [], totalUnread = 0, markAllRead } = useActivity() || {};
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="icon-btn"
        aria-label={totalUnread ? `Notifications, ${totalUnread} unread` : "Notifications"}
        aria-expanded={open}
        data-testid={testId}
      >
        <Bell size={17} />
        {totalUnread > 0 && (
          <span className="badge-count absolute -top-1.5 -right-1.5" data-testid={`${testId}-count`}>
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        )}
      </button>
      {open && (
        <div
          className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 top-[64px] sm:top-auto sm:mt-2 sm:w-[380px] z-50 bg-card border border-border slide-up"
          role="dialog"
          aria-label="Notifications"
          data-testid="notif-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div>
              <div className="font-extrabold text-[15px]">Room activity</div>
              <div className="text-xs text-muted-foreground">{totalUnread ? `${totalUnread} unread message${totalUnread === 1 ? "" : "s"}` : "You're all caught up"}</div>
            </div>
            {totalUnread > 0 && (
              <button onClick={markAllRead} className="btn-ghost text-xs" data-testid="notif-mark-all-read">
                <CheckCheck size={14} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-border">
            {notifications.length === 0 ? (
              <div className="px-5 py-10 text-center" data-testid="notif-empty">
                <div className="text-3xl" aria-hidden>✊</div>
                <p className="mt-2 text-sm font-semibold">Nothing new right now</p>
                <p className="text-xs text-muted-foreground mt-1">New messages from rooms you&apos;ve joined will show up here.</p>
              </div>
            ) : notifications.map((n) => (
              <Link
                key={`${n.product_id}-${n.scope}`}
                to={`/${n.product_slug || n.product_id}`}
                onClick={() => setOpen(false)}
                className="flex gap-3 px-4 py-3 hover:bg-raised transition-colors"
                data-testid={`notif-item-${n.product_id}`}
              >
                <span className="badge-count mt-0.5 shrink-0">{n.unread_count}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-bold truncate">{n.product_name}</span>
                  <span className="block text-[13px] text-muted-foreground line-clamp-2">
                    <strong className="text-foreground font-semibold">{n.latest_alias}:</strong> {n.latest_text}
                  </span>
                  <span className="block text-xs text-muted-foreground mt-1">{timeAgo(n.latest_at)}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
